import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import { DAYS_OF_WEEK } from "@shared/types/foyer";
import type {
  OpeningHours,
  PremiseAudience,
  PremiseConfig,
} from "@shared/types/foyer";
import { weeklyOpenMinutes } from "@shared/opening-hours";
import { useTranslation } from "react-i18next";
import { useFormat } from "../i18n/format";
import CommitDrawer from "./CommitDrawer";
import OpeningHoursFields, {
  dayLabel,
  hasInvalidTimes,
} from "./OpeningHoursFields";
import type { PremiseCommit } from "../context/PremiseWorkspace";

/**
 * Opening hours & footfall on the Config page: a read-only summary with an
 * Edit button. The form lives in a drawer whose Save writes the hours and the
 * footfall figures together (they are priced together, see
 * {@link OpeningHoursFields}) and toasts - no page-level Save.
 */
export default function OpeningHoursCard({
  premise,
  onCommit,
}: {
  premise: PremiseConfig;
  onCommit: PremiseCommit;
}) {
  const { t } = useTranslation("premises");
  const fmt = useFormat();
  const [editing, setEditing] = useState(false);
  const hours = premise.openingHours;
  const audience = premise.audience;
  const openDays = DAYS_OF_WEEK.filter((d) => hours?.[d]);
  const weekly = Math.round((weeklyOpenMinutes(hours) / 60) * 10) / 10;
  return (
    <Card variant="outlined" data-testid="opening-hours-card">
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="h6">{t("openingHoursCard.title")}</Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setEditing(true)}
            data-testid="opening-hours-edit"
          >
            {t("openingHoursCard.editHours")}
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {premise.address?.timezone
            ? t("openingHoursCard.timezoneNoteWithZone", {
                zone: premise.address.timezone,
              })
            : t("openingHoursCard.timezoneNote")}
        </Typography>
        {openDays.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("openingHoursCard.noHours")}
          </Typography>
        ) : (
          <Box data-testid="opening-hours-summary">
            {openDays.map((day) => {
              const d = hours![day]!;
              return (
                <Typography key={day} variant="body2">
                  {t("openingHoursCard.dayHours", {
                    day: dayLabel(fmt, day),
                    hours: d.allDay
                      ? t("openingHoursFields.allDay")
                      : t("openingHoursCard.opensToCloses", {
                          opens: d.opens,
                          closes: d.closes,
                        }),
                  })}
                </Typography>
              );
            })}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              {t("openingHoursCard.weeklyOpen", {
                hours: fmt.number(weekly),
              })}
            </Typography>
          </Box>
        )}
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          {t("openingHoursCard.footfallSummary", {
            visitors:
              audience?.entriesPerWeek !== undefined
                ? fmt.number(audience.entriesPerWeek)
                : t("openingHoursCard.notSet"),
            dwell:
              audience?.dwellMinutes !== undefined
                ? t("openingHoursCard.minutes", {
                    minutes: fmt.number(audience.dwellMinutes),
                  })
                : t("openingHoursCard.notSet"),
          })}
        </Typography>
      </CardContent>
      {editing && (
        <OpeningHoursDrawer
          premise={premise}
          onClose={() => setEditing(false)}
          onCommit={onCommit}
        />
      )}
    </Card>
  );
}

function OpeningHoursDrawer({
  premise,
  onClose,
  onCommit,
}: {
  premise: PremiseConfig;
  onClose: () => void;
  onCommit: PremiseCommit;
}) {
  const { t } = useTranslation("premises");
  const [hours, setHours] = useState<OpeningHours | undefined>(
    premise.openingHours
  );
  const [audience, setAudience] = useState<PremiseAudience | undefined>(
    premise.audience
  );
  return (
    <CommitDrawer
      title={t("openingHoursCard.editTitle")}
      subtitle={premise.premiseName}
      onClose={onClose}
      saveDisabled={hasInvalidTimes(hours)}
      data-testid="opening-hours-drawer"
      onSave={() =>
        onCommit(
          (c) => {
            const next = { ...c };
            if (hours) next.openingHours = hours;
            else delete next.openingHours;
            if (audience) next.audience = audience;
            else delete next.audience;
            return next;
          },
          {
            success: t("openingHoursCard.updated", {
              name: premise.premiseName,
            }),
            failure: "saveOpeningHoursFor",
            failureParams: { name: premise.premiseName },
          }
        )
      }
    >
      <OpeningHoursFields
        hours={hours}
        audience={audience}
        onChange={(next) => {
          if ("openingHours" in next) setHours(next.openingHours);
          if ("audience" in next) setAudience(next.audience);
        }}
      />
    </CommitDrawer>
  );
}
